import { css } from '@emotion/react'

import { useActiveStoreView } from '@emico/storeviews'

import StoreCard from './StoreCard'
import styles from './Stores.module.scss'
import { SrsStore } from './useSrsStore'
import { StoreFilter, useSrsStores } from './useSrsStores'
import Container from '../layout/Container'
import Grid from '../layout/Grid'
import GridItem from '../layout/Grid/GridItem'
import Heading from '../typography/Heading'

interface Props {
    title: string
    storeView: StoreFilter
    description?: string
}

/**
 * Applies storecode based filtering to the list of SRS stores
 *
 * @param storeCode store code of the website (e.g. pme_nl)
 * @param stores list of SRS stores
 * @returns list of SRS stores filtered by country code
 */
const filterStores = (storeCode: string, stores: SrsStore[]): SrsStore[] => {
    switch (storeCode) {
        case 'pme_nl':
            return stores.filter(
                (store) => store.countryCode?.toLowerCase() === 'nl',
            )
        case 'pme_be':
        case 'pme_be_fr':
            return stores.filter(
                (store) => store.countryCode?.toLowerCase() === 'be',
            )
        case 'pme_de':
            return stores.filter(
                (store) => store.countryCode?.toLowerCase() === 'de',
            )
        default:
            return stores
    }
}

const Stores = ({ storeView, title, description }: Props) => {
    const { code: storeCode } = useActiveStoreView()

    const { data: stores } = useSrsStores({
        pageSize: 200,
        filters: {
            pme_store: storeView === 'pme',
            jb_store: storeView === 'justbrands',
            ci_store: storeView === 'castiron',
        },
    })

    const filteredStores = filterStores(storeCode, stores)

    if (filteredStores.length === 0) {
        return null
    }

    return (
        <Container>
            <section className={styles.section}>
                <Heading
                    variant="h1"
                    element="h1"
                    css={css`
                        text-align: center;
                    `}
                >
                    {title}
                </Heading>
                {description && (
                    <Heading
                        variant="h3"
                        element="h2"
                        css={css`
                            text-align: center;
                        `}
                    >
                        {description}
                    </Heading>
                )}
                <Grid xs={1} sm={2} md={3} lg={4} className={styles.grid}>
                    {filteredStores
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((store) => (
                            <GridItem key={store.storeId}>
                                <StoreCard
                                    store={store}
                                    flagshipStore
                                    storeView={storeView}
                                />
                            </GridItem>
                        ))}
                </Grid>
            </section>
        </Container>
    )
}

export default Stores
